import { Micro, PhaseTrack } from '../primitives';
import type { ProactiveTriggerType } from '../../lib/proactiveCopy';
import { proactiveTypeReadable } from '../../lib/proactiveKicker';
import { PhaseLabelsV2 } from './NoRaceCard';
import { daysToGoLabel, goalLineLabel, longRaceDate, racePhaseKey, type RaceWithGoal } from './ChampionshipCard';
import styles from './ChampionshipCardDesktop.module.css';

type ChampionshipCardDesktopProps = {
  race: RaceWithGoal;
  /** When true, show the motivational coach embedded block below the
   *  phase track (desktop variant B). */
  withMotivation?: boolean;
  /**
   * Optional click handler for the "Season plan ›" CTA at the bottom.
   * Phase 5 PR 5.2: routes/today.tsx wires this to navigate('/plan').
   * Defaults undefined so existing demo / storybook usage and tests
   * don't break — the button just stays inert when no handler is
   * supplied.
   */
  onSeasonPlanClick?: () => void;
  /**
   * Phase 6 cross-cutting PR 6 — when a proactive trigger fired this
   * load, surface the cross-module handoff by replacing the
   * "Motivational coach" kicker with `RELATED · <type-readable>` and
   * adding a `--today` left-rule to the moti block. Mirrors the
   * design call in `direction.md §4` PRESENTATION row 5.
   *
   * The kicker is the user-visible side of PR 4f's prompt biasing:
   * PR 4f makes today's LLM message theme around the trigger; this
   * badge tells the user *why* today's message feels personal.
   *
   * Null/undefined → renders the default "Motivational coach" kicker
   * with no left-rule (backward compatible with all existing callers).
   */
  proactiveType?: ProactiveTriggerType | null;
  /**
   * 2.0 Today (W1-TODAY-CARDS, TC-5; frame 1q on the desktop rail). Opt-in:
   * true draws the same ratified race card as the phone's (kicker, "4 days to
   * go", "Goal 19:30" under it only when the race carries the viewer's
   * `goalTimeSeconds` (W2-GOAL-LINE), phase labels, generated line), and the
   * "Season plan" text button gives
   * way to the card's arrow named "Open season plan" (string 74), wired to
   * `onSeasonPlanClick` and ABSENT without it (the B62 rule). Absent/false = the
   * legacy rail card, byte-identical.
   */
  v2?: boolean;
};

/**
 * Desktop rail variant of the Championship card. Different layout from
 * mobile: kicker + giant countdown numeral stacked at top, goal line
 * below, phase track, optional moti block, "Season plan" CTA pinned at
 * the bottom.
 *
 * Sits inside the right rail (width ~260px inside the 300px rail column).
 */
export function ChampionshipCardDesktop({
  race,
  withMotivation = true,
  onSeasonPlanClick,
  proactiveType,
  v2 = false,
}: ChampionshipCardDesktopProps) {
  const relatedLabel = proactiveTypeReadable(proactiveType);
  const hasHint = relatedLabel !== null;
  const goalLine = goalLineLabel(race.goalTimeSeconds);

  if (v2) {
    return (
      <section className={`${styles.card} ${styles.v2Card} onDark`}>
        <div className={styles.v2Top}>
          <div className={styles.v2Head}>
            <div className={styles.v2Kicker}>
              {race.name} · {race.distance} · {longRaceDate(race.date)}
            </div>
            <div className={styles.v2Count}>{daysToGoLabel(race.daysAway)}</div>
            {goalLine && <div className={styles.v2Goal}>{goalLine}</div>}
          </div>
          {onSeasonPlanClick && (
            <button
              type="button"
              className={styles.v2Go}
              onClick={onSeasonPlanClick}
              aria-label="Open season plan"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M4 2l6 5-6 5" />
              </svg>
            </button>
          )}
        </div>

        <PhaseLabelsV2 current={racePhaseKey(race.phase)} />

        {withMotivation && (
          <div className={`${styles.v2Moti} ${hasHint ? styles.v2MotiHinted : ''}`.trim()}>
            <div className={styles.v2MotiFrom}>
              <span className={styles.v2MotiDot} aria-hidden />
              {hasHint ? `RELATED · ${relatedLabel}` : 'Motivational Coach'}
            </div>
            <div className={styles.v2MotiText}>{race.motivationalLine}</div>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={`${styles.card} onDark`}>
      <Micro as="div" className={styles.kicker}>
        {race.name}
      </Micro>
      <div className={`${styles.countNum} serif tnum`}>
        {race.daysAway}
        <span className={styles.countUnit}>d</span>
      </div>
      <div className={styles.meta}>
        {race.distance} · {race.date} · {race.location}
      </div>
      <div className={styles.goal}>
        <span>Goal {race.goalTime}</span>
        <span className={styles.dot}>·</span>
        <span>{race.currentFit}</span>
      </div>

      <div className={styles.phases}>
        <PhaseTrack phase={race.phase} progressPct={race.progressPct} variant="dark" />
      </div>

      {withMotivation && (
        <div className={`${styles.moti} ${hasHint ? styles.motiHinted : ''}`}>
          <div className={styles.motiKicker}>
            <span className={styles.motiDot} aria-hidden />
            {hasHint ? `RELATED · ${relatedLabel}` : 'Motivational coach'}
          </div>
          <div className={styles.motiText}>{race.motivationalLine}</div>
        </div>
      )}

      <button
        type="button"
        className={styles.cta}
        onClick={onSeasonPlanClick}
      >
        Season plan
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M4 2l6 5-6 5" />
        </svg>
      </button>
    </section>
  );
}
